import { type Observable } from 'rxjs'
import {
  ApiClient,
  type ApiClientOptions,
  type CursorPagedResponse,
} from '@/infra/api-client'
import {
  type CreateScopeWorkRequest,
  type OCRManualInputsResource,
  type GetManualInputsRequest,
  type OCRScopeWorkResource,
  type SearchScopeWorkRequest,
  type AggregationResponseResource,
  type LinkScopeWorkManuallyRequest,
} from '@/features/jobs/api/resources/ocr-scope-work'
import { type RealtimeMessage } from '@/features/jobs/api/resources/realtime-messages'
import { toURLSearchParams } from '@/utils/url-util'

export class WunderApi {
  readonly scopeWork

  constructor(options: ApiClientOptions) {
    this.scopeWork = new ScopeWorkApi(options)
  }
}

export class ScopeWorkApi extends ApiClient {
  /**
   * Realtime scope work messages.
   */
  readonly events$: Observable<RealtimeMessage> = this._events()

  /**
   * Searches scope work.
   */
  search(
    request: SearchScopeWorkRequest,
    signal?: AbortSignal,
  ): Promise<CursorPagedResponse<OCRScopeWorkResource>> {
    return this._get(
      `v1/scope-work/search`,
      toURLSearchParams({ ...request }),
      { signal },
    )
  }

  /**
   * Gets the scope work counts of a workspace.
   */
  getAggregations(
    workspaceId: string,
    signal?: AbortSignal,
  ): Promise<AggregationResponseResource> {
    return this._get(
      `v1/scope-work/aggregations`,
      toURLSearchParams({ workspace_id: workspaceId }),
      { signal },
    )
  }

  /**
   * Gets the manual inputs of a scope work.
   */
  getManualInputs(
    request: GetManualInputsRequest,
    signal?: AbortSignal,
  ): Promise<OCRManualInputsResource> {
    return this._get(
      `v1/scope-work/manual-inputs`,
      toURLSearchParams({ ...request }),
      { signal },
    )
  }

  /**
   * Creates scope work from the uploaded files.
   */
  create(request: CreateScopeWorkRequest): Promise<void> {
    const body = new FormData()
    const { files, ...rest } = request
    body.append('meta', JSON.stringify({ ...rest }))

    files.forEach((f, i) =>
      body.append(`file${i + 1}`, f, sanitizeFileName(f.name)),
    )

    return this._post(`v1/scope-work`, undefined, undefined, {
      body,
      headers: {},
    })
  }

  /**
   * Archives a scope work.
   */
  archive(scopeWorkId: string): Promise<void> {
    return this._delete(`v1/scope-work/${encodeURIComponent(scopeWorkId)}`)
  }

  /**
   * Runs a scope work again.
   */
  rerun(scopeWorkId: string): Promise<void> {
    return this._post(
      `v1/scope-work/${encodeURIComponent(scopeWorkId)}/rerun`,
      undefined,
    )
  }

  /**
   * Links a scope work to a job manually.
   */
  linkManually(
    scopeWorkId: string,
    request: LinkScopeWorkManuallyRequest,
  ): Promise<void> {
    return this._post(
      `v1/scope-work/${encodeURIComponent(scopeWorkId)}/manual`,
      request,
    )
  }

  /**
   * Marks a scope work as reviewed.
   */
  review(scopeWorkId: string): Promise<void> {
    return this._post(
      `v1/scope-work/${encodeURIComponent(scopeWorkId)}/review`,
      undefined,
    )
  }
}

/**
 * Sanitizes a file name.
 */
function sanitizeFileName(str: string) {
  return str.replace(/\//g, '_').replace(/\\/g, '_')
}
